import { Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";

import { StatusBadge } from "@/components/shared/StatusBadge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import type {
  PendingRootInvite,
  PlatformUser,
} from "@/services/platformUsersService";
import { cn } from "@/lib/utils";

function statusVariant(
  status: string
): "success" | "warning" | "danger" | "info" {
  switch (status) {
    case "active":
      return "success";
    case "pending":
      return "warning";
    case "disabled":
    case "expired":
      return "danger";
    default:
      return "info";
  }
}

type PlatformUserMobileCardProps = {
  user: PlatformUser;
};

export function PlatformUserMobileCard({
  user: u,
}: PlatformUserMobileCardProps): JSX.Element {
  const { t } = useTranslation();

  return (
    <Card
      className={cn(
        "overflow-hidden border-border p-4 text-left",
        "bg-card"
      )}
    >
      <div className="space-y-2">
        <p className="font-medium text-foreground">
          {u.displayName?.trim() || u.email}
        </p>
        <p className="text-sm text-muted-foreground">{u.email}</p>
        <p className="text-sm text-foreground">
          {t(`users.page.role.${u.platformRole}`)}
        </p>
        <StatusBadge variant={statusVariant(u.status)}>
          {t(`users.page.status.${u.status}`)}
        </StatusBadge>
        <p className="text-xs text-muted-foreground">—</p>
      </div>
    </Card>
  );
}

type PlatformPendingInviteMobileCardProps = {
  invite: PendingRootInvite;
  isRoot: boolean;
  onResend: (id: string) => void;
  onCancel: (id: string) => void;
  resendingId: string | null;
  cancellingId: string | null;
};

export function PlatformPendingInviteMobileCard({
  invite: inv,
  isRoot,
  onResend,
  onCancel,
  resendingId,
  cancellingId,
}: PlatformPendingInviteMobileCardProps): JSX.Element {
  const { t } = useTranslation();

  return (
    <Card className="overflow-hidden border-border p-4">
      <div className="space-y-3">
        <p className="font-medium text-foreground">{inv.email}</p>
        <p className="text-sm text-foreground">
          {t(`users.page.role.${inv.platformRole}`)}
        </p>
        <StatusBadge
          variant={inv.expired ? "danger" : statusVariant("pending")}
        >
          {inv.expired
            ? t("users.page.status.expired")
            : t("users.page.status.pending")}
        </StatusBadge>
        <p className="text-sm text-muted-foreground">
          {inv.invitedByEmail ?? "—"}
        </p>
        {isRoot ? (
          <div className="flex flex-wrap gap-2 pt-1">
            <Button
              variant="outline"
              size="sm"
              type="button"
              onClick={() => onResend(inv.id)}
              disabled={resendingId === inv.id || cancellingId === inv.id}
            >
              {resendingId === inv.id ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : null}
              {t("users.page.resendInvite")}
            </Button>
            <Button
              variant="outline"
              size="sm"
              type="button"
              onClick={() => onCancel(inv.id)}
              disabled={cancellingId === inv.id || resendingId === inv.id}
            >
              {t("users.page.cancelInvite")}
            </Button>
          </div>
        ) : null}
      </div>
    </Card>
  );
}
